import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Mail } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso.slice(0, 16); }
}

export default function CommitteeComments({ candidateId, candidateName }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const cardRef = useRef(null);
  const location = useLocation();

  const load = () => api.get(`/recruit/candidates/${candidateId}/packet-comments`).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { if (candidateId) load(); }, [candidateId]);

  // Deep-link: when the URL fragment is #committee (arriving from a PACKET_COMMENT notification),
  // scroll the panel into view and pulse it briefly so the evaluator sees the new comment.
  useEffect(() => {
    if (location.hash !== "#committee" || items.length === 0) return;
    // Delay so the DOM has painted the newly-loaded items
    const t = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setHighlighted(true);
      setTimeout(() => setHighlighted(false), 2200);
    }, 250);
    return () => clearTimeout(t);
  }, [location.hash, items.length]);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    if (t.length > 500) { toast.error("Keep it to 500 characters or fewer"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/recruit/candidates/${candidateId}/packet-comments`, { comment: t });
      setText("");
      load();
      if (data.email_result?.ok && !data.email_result?.mocked) {
        toast.success(`Sent to recruiter (${data.recruiter_email})`);
      } else if (data.recruiter_email) {
        toast.success("Comment saved — will email recruiter when SMTP is connected");
      } else {
        toast.success("Comment saved");
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card
      ref={cardRef}
      className={`border-0 shadow-sm transition-all duration-500 ${highlighted ? "ring-4 ring-pink/40 shadow-xl scale-[1.005]" : ""}`}
      data-testid="committee-comments-panel"
      id="committee">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-pink" />
            <h3 className="font-semibold">Committee comments</h3>
            <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
          </div>
          <div className="text-[10px] text-neutral-500 hidden sm:block">Comments are emailed to the recruiter and included in the offer packet PDF.</div>
        </div>

        <div className="space-y-2 mb-4">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Leave a one-line note for the recruiter about ${candidateName || "this candidate"}…`}
            rows={2}
            maxLength={500}
            data-testid="committee-comment-input"
          />
          <div className="flex items-center justify-between">
            <div className="text-[10px] text-neutral-500">{text.length}/500</div>
            <Button size="sm" className="btn-primary rounded-full" onClick={submit} disabled={busy || !text.trim()} data-testid="committee-comment-submit">
              <Send className="w-3.5 h-3.5 mr-1" /> {busy ? "Sending…" : "Send comment"}
            </Button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-neutral-500 text-sm py-3 text-center border-t">
            No comments yet — be the first to weigh in.
          </div>
        ) : (
          <div className="space-y-3 border-t pt-4">
            {items.map((c, idx) => (
              <div
                key={c.comment_id}
                className={`flex gap-3 rounded-lg p-2 -m-2 transition-all ${highlighted && idx === 0 ? "bg-pink/5 ring-2 ring-pink/40" : ""}`}
                data-testid={`committee-comment-${c.comment_id}`}>
                <div className="w-8 h-8 rounded-full bg-navy text-white grid place-items-center text-[10px] font-bold shrink-0" style={{ backgroundColor: "#00214D" }}>
                  {(c.author_name || "").split(" ").map(x => x[0]).join("").slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-semibold text-neutral-900">{c.author_name}</span>
                    <span className="text-neutral-400">·</span>
                    <span className="text-neutral-500">{(c.author_role || "").replace(/_/g, " ")}</span>
                    <span className="text-neutral-400">·</span>
                    <span className="text-neutral-500">{fmtDate(c.created_at)}</span>
                    {c.email_result?.ok && !c.email_result?.mocked && (
                      <Badge className="text-[9px] bg-teal text-white border-0 gap-1"><Mail className="w-2.5 h-2.5" /> emailed</Badge>
                    )}
                  </div>
                  <p className="text-sm text-neutral-800 mt-1 leading-relaxed">{c.comment}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
